import merge from "lodash/merge";
import {
    deleteAccessoryFailure,
    deleteAccessoryRequest,
    deleteAccessorySuccess,
    getAccessoriesFailure,
    getAccessoriesRequest,
    getAccessoriesSuccess,
    postAccessoryFailure,
    postAccessoryRequest,
    postAccessorySuccess,
} from "src/store/actions/accessories";
import { getDealSuccess } from "src/store/actions/deal";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { Accessory } from "Types/api/Accessory";

export interface AccessoriesState {
    [vin: string]: {
        entities?: {
            [id: number]: Accessory;
        };
        available?: {
            loan?: number[];
            lease?: number[];
            cash?: number[];
        };
        selected?: number[];
        updating?: number[];
        isLoading?: boolean;
    };
}

type AccessoryActions =
    | ActionType<typeof getAccessoriesRequest>
    | ActionType<typeof getAccessoriesSuccess>
    | ActionType<typeof getAccessoriesFailure>
    | ActionType<typeof postAccessoryRequest>
    | ActionType<typeof postAccessorySuccess>
    | ActionType<typeof postAccessoryFailure>
    | ActionType<typeof deleteAccessoryRequest>
    | ActionType<typeof deleteAccessorySuccess>
    | ActionType<typeof deleteAccessoryFailure>
    | ActionType<typeof getDealSuccess>
    | ActionType<typeof pollingSuccess>;

const accessories = (state: AccessoriesState = {}, action: AccessoryActions): AccessoriesState => {
    switch (action.type) {
        case getAccessoriesRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    isLoading: true,
                },
            };
        case getAccessoriesSuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    entities: {
                        ...state[action.payload.vin]?.entities,
                        ...action.payload.accessories.reduce(
                            (previousValue, currentValue) => ({ ...previousValue, [currentValue.id]: currentValue }),
                            {}
                        ),
                    },
                    available: {
                        ...state[action.payload.vin]?.available,
                        [action.payload.paymentType]: action.payload.accessories.map((accessory) => accessory.id),
                    },
                    isLoading: false,
                },
            };
        case getAccessoriesFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    isLoading: false,
                },
            };
        case postAccessoryRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: [...(state[action.payload.vin]?.selected ?? []), action.payload.accessoryId],
                    updating: [...(state[action.payload.vin]?.updating ?? []), action.payload.accessoryId],
                },
            };
        case postAccessorySuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case postAccessoryFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: state[action.payload.vin]?.selected?.filter((id) => id !== action.payload.accessoryId),
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case deleteAccessoryRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: state[action.payload.vin]?.selected?.filter((id) => id !== action.payload.accessoryId),
                    updating: [...(state[action.payload.vin]?.updating ?? []), action.payload.accessoryId],
                },
            };
        case deleteAccessorySuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case deleteAccessoryFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: [...(state[action.payload.vin]?.selected ?? []), action.payload.accessoryId],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };

        case getDealSuccess.type:
        case pollingSuccess.type:
            return merge(
                state,
                action.payload.deal.vehicles?.reduce(
                    (prev, curr) => ({
                        ...prev,
                        [curr.vin]: {
                            selected: [
                                ...(curr.accessories
                                    ?.map((accessory) => accessory.id)
                                    .filter((accessoryId) => !state[curr.vin]?.updating?.includes(accessoryId)) ?? []),
                                ...(state[curr.vin]?.selected?.filter((accessoryId) =>
                                    state[curr.vin]?.updating?.includes(accessoryId)
                                ) ?? []),
                            ],
                        },
                    }),
                    {}
                )
            );
        default:
            return state;
    }
};

export default accessories;
